import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Mail, HardDrive, Calendar, Settings, Check, X, Loader2, 
  RefreshCw, Send, Inbox, Star, Trash2, File, Folder, 
  Upload, Download, Plus, Search, Clock, MapPin, Users,
  ChevronRight, ExternalLink, AlertCircle, CheckCircle2,
  FolderOpen, FileText, Image, Video, Music, Archive
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant pour afficher un email
const EmailItem = ({ email, onClick, onStar, onDelete }) => (
  <div 
    className={`p-3 border-b hover:bg-muted/50 cursor-pointer transition-colors ${!email.is_read ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start gap-3">
      <button 
        onClick={(e) => { e.stopPropagation(); onStar(); }}
        className={`mt-1 ${email.is_starred ? 'text-yellow-500' : 'text-muted-foreground hover:text-yellow-500'}`}
      >
        <Star className={`w-4 h-4 ${email.is_starred ? 'fill-current' : ''}`} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className={`text-sm truncate ${!email.is_read ? 'font-semibold' : ''}`}>
            {email.from?.split('<')[0]?.trim() || email.from}
          </span>
          <span className="text-xs text-muted-foreground">{email.date?.split(',')[0]}</span>
        </div>
        <p className={`text-sm truncate ${!email.is_read ? 'font-medium' : 'text-muted-foreground'}`}>
          {email.subject || '(Sans objet)'}
        </p>
        <p className="text-xs text-muted-foreground truncate">{email.snippet}</p>
      </div>
    </div>
  </div>
);

// Composant pour afficher un fichier Drive
const DriveFileItem = ({ file, onClick, onDownload, onDelete }) => {
  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('folder')) return <Folder className="w-5 h-5 text-yellow-500" />;
    if (mimeType?.includes('image')) return <Image className="w-5 h-5 text-green-500" />;
    if (mimeType?.includes('video')) return <Video className="w-5 h-5 text-purple-500" />;
    if (mimeType?.includes('audio')) return <Music className="w-5 h-5 text-pink-500" />;
    if (mimeType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (mimeType?.includes('spreadsheet')) return <FileText className="w-5 h-5 text-green-600" />;
    if (mimeType?.includes('document')) return <FileText className="w-5 h-5 text-blue-500" />;
    if (mimeType?.includes('zip') || mimeType?.includes('archive')) return <Archive className="w-5 h-5 text-amber-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };
  
  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };
  
  return (
    <div 
      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      {getFileIcon(file.mime_type)}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatSize(file.size)} • {new Date(file.modified_time).toLocaleDateString('fr-FR')}
        </p>
      </div>
      <div className="flex gap-1">
        {!file.is_folder && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onDownload(); }}>
            <Download className="w-4 h-4" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Composant pour afficher un événement Calendar
const CalendarEventItem = ({ event, onClick, onDelete }) => (
  <div 
    className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
    onClick={onClick}
  >
    <div className={`w-1 h-12 rounded-full ${event.color_id ? `bg-blue-500` : 'bg-primary'}`} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium">{event.summary}</p>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
        <Clock className="w-3 h-3" />
        <span>
          {event.is_all_day ? 'Journée entière' : 
            new Date(event.start).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          }
        </span>
        {event.location && (
          <>
            <MapPin className="w-3 h-3 ml-2" />
            <span className="truncate">{event.location}</span>
          </>
        )}
      </div>
    </div>
    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>
);

export default function GoogleIntegrationsPage() {
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'settings');
  const [loading, setLoading] = useState(true);
  const [societeId, setSocieteId] = useState(null);
  const [googleStatus, setGoogleStatus] = useState(null);
  
  // Gmail state
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLabel, setEmailLabel] = useState('INBOX');
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [newEmail, setNewEmail] = useState({ to: '', subject: '', body: '' });
  
  // Drive state
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([{ id: null, name: 'Mon Drive' }]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  
  // Calendar state
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({
    summary: '',
    description: '',
    location: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    all_day: false
  });
  
  // Backup configuration state
  const [backupConfig, setBackupConfig] = useState({
    enabled: true,
    frequency: 'weekly', // daily, weekly, biweekly, monthly
    include_interventions: true,
    include_parcellaire: true,
    include_crm: true,
    include_factures: true,
    include_documents: true,
    include_rh: true,
    last_backup: null,
    next_backup: null
  });
  
  // État global de toutes les sociétés et leur connexion Google
  const [allSocietesStatus, setAllSocietesStatus] = useState({
    loading: true,
    societes: [],
    total: 0,
    connected_count: 0
  });
  
  // État pour la connexion manuelle
  const [manualConnectModal, setManualConnectModal] = useState({
    open: false,
    societeId: null,
    societeName: '',
    step: 1, // 1 = instructions, 2 = entrer le code
    authUrl: '',
    code: '',
    loading: false,
    selectedRedirectUri: '',
    availableRedirectUris: []
  });
  const [savingBackup, setSavingBackup] = useState(false);
  
  // Get societe_id from localStorage or user context
  // Cette fonction récupère la société courante
  const getCurrentSocieteId = useCallback(async () => {
    // Priorité 1: activeSocieteId (défini par le sélecteur de société)
    let id = localStorage.getItem('activeSocieteId');
    
    // Fallback: societe_id
    if (!id) {
      id = localStorage.getItem('societe_id');
    }
    
    if (!id) {
      id = sessionStorage.getItem('societe_id');
    }
    
    if (!id) {
      const params = new URLSearchParams(window.location.search);
      id = params.get('societe_id');
    }
    
    if (!id) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const res = await api.get('/tenant/societes');
          if (res.data?.societes?.length > 0) {
            const defaultSociete = res.data.societes.find(s => s.is_default) || res.data.societes[0];
            id = defaultSociete.id || defaultSociete.societe_id;
            if (id) {
              localStorage.setItem('activeSocieteId', id);
            }
          }
        }
      } catch (e) {
        console.error('Erreur récupération société:', e);
      }
    }
    return id;
  }, []);

  // Écouter les changements de société (via localStorage event)
  useEffect(() => {
    const loadSociete = async () => {
      const id = await getCurrentSocieteId();
      if (id && id !== societeId) {
        setSocieteId(id);
        setGoogleStatus(null); // Reset le status pour recharger
      }
      setLoading(false);
    };
    
    loadSociete();
    
    // Écouter les changements dans localStorage (quand on change de société)
    const handleStorageChange = (e) => {
      if ((e.key === 'activeSocieteId' || e.key === 'societe_id') && e.newValue) {
        setSocieteId(e.newValue);
        setGoogleStatus(null); // Reset pour recharger le status
      }
    };
    
    // Écouter aussi un événement custom pour les changements de société
    const handleSocieteChange = (e) => {
      if (e.detail?.societe_id) {
        setSocieteId(e.detail.societe_id);
        setGoogleStatus(null);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('societe-changed', handleSocieteChange);
    
    // Vérifier régulièrement si la société a changé (fallback)
    const interval = setInterval(async () => {
      const currentId = localStorage.getItem('activeSocieteId') || localStorage.getItem('societe_id');
      if (currentId && currentId !== societeId) {
        setSocieteId(currentId);
        setGoogleStatus(null);
      }
    }, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('societe-changed', handleSocieteChange);
      clearInterval(interval);
    };
  }, [societeId, getCurrentSocieteId]);

  // Check URL params for OAuth callback (séparé)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      toast.success(`Compte Google connecté: ${params.get('email')}`);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('error')) {
      toast.error(`Erreur de connexion: ${params.get('error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  
  // Fetch Google connection status
  const fetchGoogleStatus = useCallback(async () => {
    if (!societeId) {
      setLoading(false);
      return;
    }
    
    // Récupérer le user_id courant
    let currentUserId = '';
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        currentUserId = userData.id || '';
      }
    } catch (e) {}
    
    try {
      const params = currentUserId ? `?user_id=${currentUserId}` : '';
      const res = await api.get(`/google/status/${societeId}${params}`);
      setGoogleStatus(res.data);
    } catch (error) {
      console.error('Error fetching Google status:', error);
    }
    setLoading(false);
  }, [societeId]);
  
  useEffect(() => {
    fetchGoogleStatus();
  }, [fetchGoogleStatus]);
  
  // Charger l'état de toutes les sociétés au démarrage
  useEffect(() => {
    refreshAllSocietesStatus();
  }, []);
  
  // Connect Google account
  const connectGoogle = async () => {
    // Récupérer ou définir le societe_id - priorité à activeSocieteId
    let currentSocieteId = societeId || localStorage.getItem('activeSocieteId');
    
    // Récupérer le user_id courant
    let currentUserId = '';
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        currentUserId = userData.id || '';
      }
    } catch (e) {
      console.error('Erreur récupération user:', e);
    }
    
    if (!currentSocieteId) {
      // Essayer de récupérer la société par défaut
      try {
        const res = await api.get('/tenant/societes');
        if (res.data?.societes?.length > 0) {
          const defaultSociete = res.data.societes.find(s => s.is_default) || res.data.societes[0];
          currentSocieteId = defaultSociete.id || defaultSociete.societe_id;
          if (currentSocieteId) {
            localStorage.setItem('activeSocieteId', currentSocieteId);
            setSocieteId(currentSocieteId);
          }
        }
      } catch (e) {
        console.error('Erreur récupération société:', e);
      }
    }
    
    if (!currentSocieteId) {
      toast.error('Impossible de trouver votre société. Reconnectez-vous à l\'application.');
      return;
    }
    
    try {
      const params = new URLSearchParams({ societe_id: currentSocieteId });
      if (currentUserId) params.append('user_id', currentUserId);
      params.append('origin', window.location.origin);
      const res = await api.get(`/google/auth/start?${params.toString()}`);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      window.location.href = res.data.authorization_url;
    } catch (error) {
      console.error('Error starting Google auth:', error);
      toast.error('Erreur lors de la connexion Google');
    }
  };
  
  // Disconnect Google account
  const disconnectGoogle = async () => {
    if (!societeId) return;
    
    // Récupérer le user_id courant
    let currentUserId = '';
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        currentUserId = userData.id || '';
      }
    } catch (e) {}
    
    try {
      const params = currentUserId ? `?user_id=${currentUserId}` : '';
      await api.delete(`/google/disconnect/${societeId}${params}`);
      setGoogleStatus(null);
      toast.success('Compte Google déconnecté');
      fetchGoogleStatus();
      refreshAllSocietesStatus();
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  };
  
  // Récupérer l'état de connexion de TOUTES les sociétés
  const refreshAllSocietesStatus = async () => {
    setAllSocietesStatus(prev => ({ ...prev, loading: true }));
    try {
      const res = await api.get('/google/all-societes-status');
      setAllSocietesStatus({
        loading: false,
        societes: res.data.societes || [],
        total: res.data.total || 0,
        connected_count: res.data.connected_count || 0
      });
    } catch (error) {
      console.error('Error fetching all societes status:', error);
      setAllSocietesStatus(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Connecter une société spécifique à Google
  const connectSocieteGoogle = async (targetSocieteId) => {
    try {
      const res = await api.get(`/google/auth/start?societe_id=${targetSocieteId}`);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      window.location.href = res.data.authorization_url;
    } catch (error) {
      console.error('Error starting Google auth for societe:', error);
      toast.error('Erreur lors de la connexion Google');
    }
  };
  
  // Déconnecter une société spécifique de Google
  const disconnectSocieteGoogle = async (targetSocieteId) => {
    try {
      await api.delete(`/google/disconnect/${targetSocieteId}`);
      toast.success('Compte Google déconnecté');
      refreshAllSocietesStatus();
      // Si c'est la société courante, mettre à jour le statut
      if (targetSocieteId === societeId) {
        fetchGoogleStatus();
      }
    } catch (error) {
      console.error('Error disconnecting societe:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  };
  
  // Ouvrir le modal de connexion manuelle
  const openManualConnectModal = async (targetSocieteId, societeName) => {
    setManualConnectModal({
      open: true,
      societeId: targetSocieteId,
      societeName: societeName,
      step: 1,
      authUrl: '',
      code: '',
      loading: true,
      selectedRedirectUri: '',
      availableRedirectUris: []
    });
    
    try {
      // Récupérer l'URL d'autorisation
      const res = await api.get(`/google/auth/manual-start?societe_id=${targetSocieteId}`);
      setManualConnectModal(prev => ({
        ...prev,
        authUrl: res.data.authorization_url,
        selectedRedirectUri: res.data.redirect_uri,
        availableRedirectUris: res.data.available_redirect_uris || [],
        loading: false
      }));
    } catch (error) {
      console.error('Error starting manual auth:', error);
      toast.error('Erreur lors de la génération du lien');
      setManualConnectModal(prev => ({ ...prev, loading: false, open: false }));
    }
  };
  
  // Changer le redirect_uri et régénérer l'URL
  const changeRedirectUri = async (newUri) => {
    const { societeId } = manualConnectModal;
    setManualConnectModal(prev => ({ ...prev, loading: true }));
    
    try {
      const res = await api.get(`/google/auth/manual-start?societe_id=${societeId}&redirect_uri=${encodeURIComponent(newUri)}`);
      setManualConnectModal(prev => ({
        ...prev,
        authUrl: res.data.authorization_url,
        selectedRedirectUri: res.data.redirect_uri,
        loading: false
      }));
      toast.success('Lien mis à jour');
    } catch (error) {
      console.error('Error changing redirect URI:', error);
      toast.error('Erreur lors du changement');
      setManualConnectModal(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Finaliser la connexion manuelle
  const completeManualConnect = async () => {
    const { societeId, code } = manualConnectModal;
    
    if (!code.trim()) {
      toast.error('Veuillez entrer le code d\'autorisation');
      return;
    }
    
    setManualConnectModal(prev => ({ ...prev, loading: true }));
    
    try {
      const res = await api.post('/google/auth/manual-complete', {
        societe_id: societeId,
        code: code.trim(),
        redirect_uri: 'https://bureauagricole.fr/api/google/auth/callback'
      });
      
      toast.success(`Compte connecté: ${res.data.email}`);
      setManualConnectModal({ open: false, societeId: null, societeName: '', step: 1, authUrl: '', code: '', loading: false });
      refreshAllSocietesStatus();
      
      if (societeId === societeId) {
        fetchGoogleStatus();
      }
    } catch (error) {
      console.error('Error completing manual auth:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la connexion');
      setManualConnectModal(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Copier l'URL dans le presse-papier
  const copyAuthUrl = () => {
    navigator.clipboard.writeText(manualConnectModal.authUrl);
    toast.success('URL copiée !');
  };
  
  // ==================== GMAIL ====================
  const fetchEmails = async () => {
    if (!societeId || !googleStatus?.connected) return;
    
    setLoadingEmails(true);
    try {
      const res = await api.get(`/gmail/messages/${societeId}?label=${emailLabel}&max_results=30`);
      setEmails(res.data.emails || []);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Erreur lors du chargement des emails');
    }
    setLoadingEmails(false);
  };
  
  const sendEmail = async () => {
    try {
      await api.post('/gmail/send', {
        societe_id: societeId,
        to: newEmail.to,
        subject: newEmail.subject,
        body: newEmail.body
      });
      toast.success('Email envoyé !');
      setShowComposeDialog(false);
      setNewEmail({ to: '', subject: '', body: '' });
      fetchEmails();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Erreur lors de l\'envoi');
    }
  };
  
  const toggleStar = async (emailId) => {
    try {
      const email = emails.find(e => e.id === emailId);
      await api.post(`/gmail/star/${societeId}/${emailId}?starred=${!email.is_starred}`);
      fetchEmails();
    } catch (error) {
      console.error('Error toggling star:', error);
    }
  };
  
  // ==================== DRIVE ====================
  const fetchFiles = async (folderId = null) => {
    if (!societeId || !googleStatus?.connected) return;
    
    setLoadingFiles(true);
    try {
      const url = folderId 
        ? `/drive/files/${societeId}?folder_id=${folderId}`
        : `/drive/files/${societeId}`;
      const res = await api.get(url);
      setFiles(res.data.files || []);
      setCurrentFolder(folderId);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Erreur lors du chargement des fichiers');
    }
    setLoadingFiles(false);
  };
  
  const navigateToFolder = (folder) => {
    if (folder.is_folder) {
      setFolderPath([...folderPath, { id: folder.id, name: folder.name }]);
      fetchFiles(folder.id);
    } else if (folder.web_view_link) {
      window.open(folder.web_view_link, '_blank');
    }
  };
  
  const navigateBack = (index) => {
    const newPath = folderPath.slice(0, index + 1);
    setFolderPath(newPath);
    fetchFiles(newPath[newPath.length - 1].id);
  };
  
  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await fetch(`${API_URL}/api/drive/download/${societeId}/${fileId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement démarré');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };
  
  const deleteFile = async (fileId) => {
    if (!confirm('Supprimer ce fichier ?')) return;
    
    try {
      await api.delete(`/drive/delete/${societeId}/${fileId}`);
      toast.success('Fichier supprimé');
      fetchFiles(currentFolder);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // ==================== CALENDAR ====================
  const fetchEvents = async () => {
    if (!societeId || !googleStatus?.connected) return;
    
    setLoadingEvents(true);
    try {
      const res = await api.get(`/calendar/upcoming/${societeId}?days=30`);
      setEvents(res.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Erreur lors du chargement des événements');
    }
    setLoadingEvents(false);
  };
  
  const createEvent = async () => {
    try {
      const eventData = {
        societe_id: societeId,
        summary: newEvent.summary,
        description: newEvent.description,
        location: newEvent.location,
        start: newEvent.all_day 
          ? { date: newEvent.start_date, timeZone: 'Europe/Paris' }
          : { dateTime: `${newEvent.start_date}T${newEvent.start_time}:00`, timeZone: 'Europe/Paris' },
        end: newEvent.all_day
          ? { date: newEvent.end_date || newEvent.start_date, timeZone: 'Europe/Paris' }
          : { dateTime: `${newEvent.end_date || newEvent.start_date}T${newEvent.end_time || newEvent.start_time}:00`, timeZone: 'Europe/Paris' }
      };
      
      await api.post('/calendar/event', eventData);
      toast.success('Événement créé !');
      setShowEventDialog(false);
      setNewEvent({ summary: '', description: '', location: '', start_date: '', start_time: '', end_date: '', end_time: '', all_day: false });
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Erreur lors de la création');
    }
  };
  
  const deleteEvent = async (eventId) => {
    if (!confirm('Supprimer cet événement ?')) return;
    
    try {
      await api.delete(`/calendar/event/${societeId}/${eventId}`);
      toast.success('Événement supprimé');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Load data when tab changes
  useEffect(() => {
    if (googleStatus?.connected) {
      if (activeTab === 'gmail') fetchEmails();
      if (activeTab === 'drive') fetchFiles();
      if (activeTab === 'calendar') fetchEvents();
    }
  }, [activeTab, googleStatus?.connected, emailLabel, societeId]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6 p-6" data-testid="google-integrations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Intégrations Google
          </h1>
          <p className="text-muted-foreground">Gmail, Drive et Calendar connectés à votre exploitation</p>
        </div>
        {googleStatus?.connected && (
          <div className="flex items-center gap-2">
            <img src={googleStatus.picture} alt="" className="w-8 h-8 rounded-full" />
            <div className="text-right">
              <p className="text-sm font-medium">{googleStatus.name}</p>
              <p className="text-xs text-muted-foreground">{googleStatus.email}</p>
            </div>
          </div>
        )}
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Connexion
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2">
            <HardDrive className="w-4 h-4" />
            Sauvegarde
          </TabsTrigger>
          <TabsTrigger value="gmail" disabled={!googleStatus?.connected} className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Gmail
          </TabsTrigger>
          <TabsTrigger value="drive" disabled={!googleStatus?.connected} className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4" />
            Drive
          </TabsTrigger>
          <TabsTrigger value="calendar" disabled={!googleStatus?.connected} className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Agenda
          </TabsTrigger>
        </TabsList>
        
        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>État de la connexion</CardTitle>
              <CardDescription>
                Connectez votre compte Google pour accéder à Gmail, Drive et Calendar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {googleStatus?.connected ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">Compte Google connecté</p>
                      <p className="text-sm text-green-600">{googleStatus.email}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <Mail className="w-5 h-5 text-red-500" />
                      <span className="text-sm">Gmail</span>
                      <Check className="w-4 h-4 text-green-500 ml-auto" />
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <HardDrive className="w-5 h-5 text-yellow-500" />
                      <span className="text-sm">Drive</span>
                      <Check className="w-4 h-4 text-green-500 ml-auto" />
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">Calendar</span>
                      <Check className="w-4 h-4 text-green-500 ml-auto" />
                    </div>
                  </div>
                  
                  <Button variant="destructive" onClick={disconnectGoogle}>
                    <X className="w-4 h-4 mr-2" />
                    Déconnecter le compte Google
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">Aucun compte connecté</p>
                      <p className="text-sm text-amber-600">Connectez votre compte Google pour utiliser ces services</p>
                    </div>
                  </div>
                  
                  <Button onClick={connectGoogle} className="w-full h-12 text-lg">
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Connecter avec Google
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Liste des exploitations et leurs emails associés - DYNAMIQUE */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Connexion des boîtes mail par société
              </CardTitle>
              <CardDescription>
                Connectez chaque société à son compte Gmail pour permettre aux agents IA de lire et envoyer des emails
              </CardDescription>
            </CardHeader>
            <CardContent>
              {allSocietesStatus.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Chargement...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Résumé */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg mb-4">
                    <span className="text-sm font-medium">
                      {allSocietesStatus.connected_count || 0} / {allSocietesStatus.total || 0} sociétés connectées
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={refreshAllSocietesStatus}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Actualiser
                    </Button>
                  </div>
                  
                  {/* Liste des sociétés */}
                  {allSocietesStatus.societes?.map((societe, idx) => (
                    <div 
                      key={societe.id || idx} 
                      className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                        societe.google_connected 
                          ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' 
                          : 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {societe.google_connected ? (
                          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{societe.nom}</p>
                          {societe.google_connected ? (
                            <p className="text-sm text-green-600 dark:text-green-400">{societe.google_email}</p>
                          ) : (
                            <p className="text-sm text-amber-600 dark:text-amber-400">Non connecté</p>
                          )}
                        </div>
                      </div>
                      
                      {societe.google_connected ? (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => disconnectSocieteGoogle(societe.id)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Déconnecter
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={() => connectSocieteGoogle(societe.id)}
                          >
                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            Connecter
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={() => openManualConnectModal(societe.id, societe.nom)}
                          >
                            Manuel
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Backup Configuration Tab */}
        <TabsContent value="backup" className="space-y-4">
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Sécurité de vos données
              </CardTitle>
              <CardDescription>
                Vos données sont sauvegardées automatiquement sur Google Drive pour garantir leur sécurité
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-white/50 dark:bg-black/20 rounded-lg text-center">
                  <HardDrive className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-medium">Stockage sécurisé</p>
                  <p className="text-sm text-muted-foreground">Google Drive chiffré</p>
                </div>
                <div className="p-4 bg-white/50 dark:bg-black/20 rounded-lg text-center">
                  <RefreshCw className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="font-medium">Sauvegarde automatique</p>
                  <p className="text-sm text-muted-foreground">{
                    backupConfig.frequency === 'daily' ? 'Chaque jour' :
                    backupConfig.frequency === 'weekly' ? 'Chaque semaine' :
                    backupConfig.frequency === 'biweekly' ? 'Tous les 15 jours' :
                    'Chaque mois'
                  }</p>
                </div>
                <div className="p-4 bg-white/50 dark:bg-black/20 rounded-lg text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                  <p className="font-medium">Dernière sauvegarde</p>
                  <p className="text-sm text-muted-foreground">
                    {backupConfig.last_backup ? new Date(backupConfig.last_backup).toLocaleDateString('fr-FR') : 'Jamais'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Configuration des sauvegardes</CardTitle>
              <CardDescription>
                Choisissez la fréquence et les données à sauvegarder
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Fréquence */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Fréquence de sauvegarde</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'daily', label: 'Quotidien', desc: 'Tous les jours' },
                    { value: 'weekly', label: 'Hebdomadaire', desc: 'Chaque semaine' },
                    { value: 'biweekly', label: 'Bi-mensuel', desc: 'Tous les 15 jours' },
                    { value: 'monthly', label: 'Mensuel', desc: 'Chaque mois' },
                  ].map(freq => (
                    <div
                      key={freq.value}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        backupConfig.frequency === freq.value 
                          ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                          : 'hover:border-primary/50'
                      }`}
                      onClick={() => setBackupConfig({...backupConfig, frequency: freq.value})}
                    >
                      <p className="font-medium">{freq.label}</p>
                      <p className="text-xs text-muted-foreground">{freq.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Données à sauvegarder */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Données à sauvegarder</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'include_interventions', label: 'Interventions', icon: '🚜' },
                    { key: 'include_parcellaire', label: 'Parcellaire', icon: '🗺️' },
                    { key: 'include_crm', label: 'CRM & Contacts', icon: '👥' },
                    { key: 'include_factures', label: 'Factures', icon: '📄' },
                    { key: 'include_documents', label: 'Documents', icon: '📁' },
                    { key: 'include_rh', label: 'RH & Paie', icon: '👔' },
                  ].map(item => (
                    <div
                      key={item.key}
                      className={`p-3 border rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                        backupConfig[item.key] 
                          ? 'border-green-500 bg-green-50 dark:bg-green-950/20' 
                          : 'border-muted opacity-60'
                      }`}
                      onClick={() => setBackupConfig({...backupConfig, [item.key]: !backupConfig[item.key]})}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                      {backupConfig[item.key] && <Check className="w-4 h-4 text-green-600 ml-auto" />}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                <Button 
                  onClick={async () => {
                    setSavingBackup(true);
                    try {
                      await api.post('/drive/backup/config', backupConfig);
                      toast.success('Configuration sauvegardée');
                    } catch (e) {
                      toast.error('Erreur de sauvegarde');
                    }
                    setSavingBackup(false);
                  }}
                  disabled={savingBackup}
                >
                  {savingBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Enregistrer la configuration
                </Button>
                <Button 
                  variant="outline"
                  onClick={async () => {
                    toast.info('Sauvegarde manuelle en cours...');
                    try {
                      await api.post('/drive/backup/now');
                      toast.success('Sauvegarde lancée avec succès');
                    } catch (e) {
                      toast.error('Erreur lors de la sauvegarde');
                    }
                  }}
                  disabled={!googleStatus?.connected}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Sauvegarder maintenant
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Architecture Drive */}
          <Card>
            <CardHeader>
              <CardTitle>Architecture de stockage sur Drive</CardTitle>
              <CardDescription>
                Vos fichiers sont organisés automatiquement selon cette structure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="font-mono text-sm bg-muted p-4 rounded-lg space-y-1">
                <p className="text-primary font-bold">📁 Bureau Agricole/</p>
                <p className="ml-4">📁 [Nom de votre société]/</p>
                <p className="ml-8">📁 Factures/</p>
                <p className="ml-8">📁 Documents Réglementaires/</p>
                <p className="ml-8">📁 RH/</p>
                <p className="ml-12">📁 Contrats/</p>
                <p className="ml-12">📁 Fiches de paie/</p>
                <p className="ml-8">📁 Transport/</p>
                <p className="ml-12">📁 Bons de Livraison/</p>
                <p className="ml-8">📁 Réglementaire/</p>
                <p className="ml-12">📁 Registre Phyto/</p>
                <p className="ml-8">📁 Certifications/</p>
                <p className="ml-8">📁 Sauvegardes/</p>
                <p className="ml-12">📄 backup_2024-01-15.json</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Gmail Tab */}
        <TabsContent value="gmail" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Select value={emailLabel} onValueChange={setEmailLabel}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INBOX">Boîte de réception</SelectItem>
                  <SelectItem value="SENT">Envoyés</SelectItem>
                  <SelectItem value="STARRED">Favoris</SelectItem>
                  <SelectItem value="TRASH">Corbeille</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={fetchEmails}>
                <RefreshCw className={`w-4 h-4 ${loadingEmails ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <Button onClick={() => setShowComposeDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau message
            </Button>
          </div>
          
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                {loadingEmails ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : emails.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                    <Inbox className="w-12 h-12 mb-2 opacity-30" />
                    <p>Aucun email</p>
                  </div>
                ) : (
                  emails.map(email => (
                    <EmailItem 
                      key={email.id} 
                      email={email}
                      onClick={() => setSelectedEmail(email)}
                      onStar={() => toggleStar(email.id)}
                      onDelete={() => {}}
                    />
                  ))
                )}
              </ScrollArea>
            </CardContent>
          </Card>
          
          {/* Compose Dialog */}
          <Dialog open={showComposeDialog} onOpenChange={setShowComposeDialog}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Nouveau message</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>À</Label>
                  <Input 
                    type="email"
                    value={newEmail.to}
                    onChange={(e) => setNewEmail({...newEmail, to: e.target.value})}
                    placeholder="destinataire@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Objet</Label>
                  <Input 
                    value={newEmail.subject}
                    onChange={(e) => setNewEmail({...newEmail, subject: e.target.value})}
                    placeholder="Objet du message"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea 
                    value={newEmail.body}
                    onChange={(e) => setNewEmail({...newEmail, body: e.target.value})}
                    placeholder="Votre message..."
                    rows={8}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowComposeDialog(false)}>Annuler</Button>
                <Button onClick={sendEmail} disabled={!newEmail.to || !newEmail.subject}>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Email Detail Dialog */}
          <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
            <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="text-lg pr-8">{selectedEmail?.subject || '(Sans objet)'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                {/* Email metadata */}
                <div className="flex flex-col gap-1 text-sm bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-muted-foreground">De:</span>{' '}
                      <span className="font-medium">{selectedEmail?.from}</span>
                    </div>
                    <span className="text-muted-foreground text-xs">{selectedEmail?.date}</span>
                  </div>
                  {selectedEmail?.to && (
                    <div>
                      <span className="font-medium text-muted-foreground">À:</span> {selectedEmail.to}
                    </div>
                  )}
                  {selectedEmail?.cc && (
                    <div>
                      <span className="font-medium text-muted-foreground">Cc:</span> {selectedEmail.cc}
                    </div>
                  )}
                </div>
                
                {/* Attachments section */}
                {selectedEmail?.attachments && selectedEmail.attachments.length > 0 && (
                  <div className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center gap-2 mb-2">
                      <File className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{selectedEmail.attachments.length} pièce(s) jointe(s)</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmail.attachments.map((att, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          className="h-auto py-2 px-3"
                          onClick={async () => {
                            try {
                              toast.info('Téléchargement en cours...');
                              const response = await api.get(
                                `/gmail/attachment/${societeId}/${selectedEmail.id}/${att.id}`
                              );
                              if (response.data?.data) {
                                // Create blob and download
                                const byteCharacters = atob(response.data.data);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: att.mime_type });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = att.filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                toast.success('Pièce jointe téléchargée');
                              }
                            } catch (error) {
                              console.error('Download error:', error);
                              toast.error('Erreur lors du téléchargement');
                            }
                          }}
                        >
                          <Download className="w-3 h-3 mr-2" />
                          <span className="max-w-[150px] truncate">{att.filename}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({att.size > 1024*1024 
                              ? `${(att.size/1024/1024).toFixed(1)} MB` 
                              : `${Math.round(att.size/1024)} KB`})
                          </span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                <Separator />
                
                {/* Email body with scroll */}
                <ScrollArea className="flex-1 min-h-[200px] max-h-[400px]">
                  <div className="prose prose-sm max-w-none dark:prose-invert pr-4">
                    {selectedEmail?.body_html ? (
                      <div 
                        dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                        className="email-content"
                      />
                    ) : selectedEmail?.body ? (
                      <pre className="whitespace-pre-wrap font-sans text-sm">{selectedEmail.body}</pre>
                    ) : (
                      <p className="text-muted-foreground italic">Aucun contenu à afficher</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Actions footer */}
              <DialogFooter className="mt-4 pt-4 border-t">
                <Button variant="outline" onClick={() => setSelectedEmail(null)}>
                  Fermer
                </Button>
                <Button onClick={() => {
                  setNewEmail({
                    to: selectedEmail?.from?.match(/<(.+)>/)?.[1] || selectedEmail?.from || '',
                    subject: `Re: ${selectedEmail?.subject || ''}`,
                    body: `\n\n--- Message original ---\nDe: ${selectedEmail?.from}\nDate: ${selectedEmail?.date}\n\n${selectedEmail?.body || ''}`
                  });
                  setSelectedEmail(null);
                  setShowComposeDialog(true);
                }}>
                  <Send className="w-4 h-4 mr-2" />
                  Répondre
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
        
        {/* Drive Tab */}
        <TabsContent value="drive" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {folderPath.map((folder, idx) => (
                <div key={idx} className="flex items-center">
                  {idx > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground mx-1" />}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigateBack(idx)}
                    className={idx === folderPath.length - 1 ? 'font-medium' : ''}
                  >
                    {folder.name}
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => fetchFiles(currentFolder)}>
                <RefreshCw className={`w-4 h-4 ${loadingFiles ? 'animate-spin' : ''}`} />
              </Button>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Importer
              </Button>
            </div>
          </div>
          
          <Card>
            <CardContent className="p-4">
              {loadingFiles ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : files.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <FolderOpen className="w-12 h-12 mb-2 opacity-30" />
                  <p>Dossier vide</p>
                </div>
              ) : (
                <div className="grid gap-2">
                  {/* Folders first */}
                  {files.filter(f => f.is_folder).map(file => (
                    <DriveFileItem 
                      key={file.id}
                      file={file}
                      onClick={() => navigateToFolder(file)}
                      onDownload={() => {}}
                      onDelete={() => deleteFile(file.id)}
                    />
                  ))}
                  {/* Then files */}
                  {files.filter(f => !f.is_folder).map(file => (
                    <DriveFileItem 
                      key={file.id}
                      file={file}
                      onClick={() => navigateToFolder(file)}
                      onDownload={() => downloadFile(file.id, file.name)}
                      onDelete={() => deleteFile(file.id)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Événements à venir</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={fetchEvents}>
                <RefreshCw className={`w-4 h-4 ${loadingEvents ? 'animate-spin' : ''}`} />
              </Button>
              <Button onClick={() => setShowEventDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvel événement
              </Button>
            </div>
          </div>
          
          <Card>
            <CardContent className="p-4">
              {loadingEvents ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : events.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <Calendar className="w-12 h-12 mb-2 opacity-30" />
                  <p>Aucun événement à venir</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {events.map(event => (
                    <CalendarEventItem 
                      key={event.id}
                      event={event}
                      onClick={() => event.html_link && window.open(event.html_link, '_blank')}
                      onDelete={() => deleteEvent(event.id)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Create Event Dialog */}
          <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Nouvel événement</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Titre *</Label>
                  <Input 
                    value={newEvent.summary}
                    onChange={(e) => setNewEvent({...newEvent, summary: e.target.value})}
                    placeholder="Titre de l'événement"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date de début *</Label>
                    <Input 
                      type="date"
                      value={newEvent.start_date}
                      onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Heure de début</Label>
                    <Input 
                      type="time"
                      value={newEvent.start_time}
                      onChange={(e) => setNewEvent({...newEvent, start_time: e.target.value})}
                      disabled={newEvent.all_day}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date de fin</Label>
                    <Input 
                      type="date"
                      value={newEvent.end_date}
                      onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Heure de fin</Label>
                    <Input 
                      type="time"
                      value={newEvent.end_time}
                      onChange={(e) => setNewEvent({...newEvent, end_time: e.target.value})}
                      disabled={newEvent.all_day}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Lieu</Label>
                  <Input 
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    placeholder="Adresse ou lieu"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    placeholder="Description..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowEventDialog(false)}>Annuler</Button>
                <Button onClick={createEvent} disabled={!newEvent.summary || !newEvent.start_date}>
                  <Check className="w-4 h-4 mr-2" />
                  Créer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
      
      {/* Modal de connexion manuelle Google */}
      <Dialog open={manualConnectModal.open} onOpenChange={(open) => !open && setManualConnectModal(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Connexion manuelle - {manualConnectModal.societeName}</DialogTitle>
          </DialogHeader>
          
          {manualConnectModal.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2">Chargement...</span>
            </div>
          ) : manualConnectModal.step === 1 ? (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Étape 1 : Autoriser l'accès</h4>
                <ol className="text-sm space-y-2 list-decimal list-inside text-muted-foreground">
                  <li>Cliquez sur "Copier le lien" ci-dessous</li>
                  <li>Ouvrez ce lien dans votre navigateur</li>
                  <li>Connectez-vous avec le compte Google souhaité</li>
                  <li>Autorisez l'accès à Bureau Agricole</li>
                  <li>Après redirection, copiez le <strong>code</strong> de l'URL</li>
                </ol>
              </div>
              
              {/* Sélecteur de redirect_uri en cas d'erreur 400 */}
              {manualConnectModal.availableRedirectUris.length > 1 && (
                <div className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                    Erreur 400 ? Essayez un autre domaine :
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {manualConnectModal.availableRedirectUris.map((uri, idx) => (
                      <Button
                        key={idx}
                        variant={uri === manualConnectModal.selectedRedirectUri ? "default" : "outline"}
                        size="sm"
                        onClick={() => changeRedirectUri(uri)}
                        className="text-xs"
                      >
                        {uri.includes('www.bureauagricole') ? 'www.bureauagricole.fr' : 
                         uri.includes('bureauagricole.fr') ? 'bureauagricole.fr' :
                         uri.includes('preview') ? 'Preview' : 'Localhost'}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Lien d'autorisation Google</Label>
                <div className="flex gap-2">
                  <Input 
                    value={manualConnectModal.authUrl}
                    readOnly
                    className="text-xs"
                  />
                  <Button variant="outline" onClick={copyAuthUrl}>
                    Copier
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Redirect URI: {manualConnectModal.selectedRedirectUri}
                </p>
              </div>
              
              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  <strong>Astuce :</strong> Après autorisation, vous serez redirigé vers une page (peut-être une erreur, c'est normal). 
                  Regardez l'URL dans la barre d'adresse : elle contient <code className="bg-green-100 dark:bg-green-900 px-1 rounded">?code=4/0A...</code>. 
                  Copiez tout ce qui suit "code=" jusqu'au prochain "&" ou la fin.
                </p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setManualConnectModal(prev => ({ ...prev, open: false }))}>
                  Annuler
                </Button>
                <Button onClick={() => setManualConnectModal(prev => ({ ...prev, step: 2 }))}>
                  J'ai le code
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Étape 2 : Entrer le code</h4>
                <p className="text-sm text-muted-foreground">
                  Collez le code d'autorisation que vous avez copié depuis l'URL de redirection.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Code d'autorisation</Label>
                <Textarea 
                  value={manualConnectModal.code}
                  onChange={(e) => setManualConnectModal(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="Collez le code ici (ex: 4/0AXEe...)"
                  rows={3}
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setManualConnectModal(prev => ({ ...prev, step: 1 }))}>
                  Retour
                </Button>
                <Button onClick={completeManualConnect} disabled={!manualConnectModal.code.trim()}>
                  <Check className="w-4 h-4 mr-2" />
                  Connecter
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
